import type { ReportAnalytics } from "@skud/domain";
import { useTranslation } from "react-i18next";
import {
	ATTENDANCE_STATUS_RANG,
	BulletBar,
	ChartCard,
	Donut,
	HBarList,
	Histogram,
	holatVar,
	Legend,
	LineChart,
	ScatterPlot,
	type ScatterPoint,
	SEQUENTIAL_BLUE,
	SegmentBar,
	Sparkline,
	StatusMatrix,
	type StatusMatrixRow,
	sequentialStep,
} from "../../../../../shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { matritsaSanalari } from "../../../utils/matrixDays";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	AnalyticsGrid,
	foizMatni100,
	kunYorlig,
	sanaQisqa,
	soatDaqiqa,
	tartiblanganHaftaKunlari,
	useBolimNomi,
} from "./shared";

/** Davomat maqsadi, foiz (canvas). */
const MAQSAD = 95;
/** KPI bullet shkalasi. */
const KPI_MIN = 80;
/** Hafta kunlari shkalasi — 90..100% (canvas, F1 bilan bir xil). */
const HAFTA_MIN = 90;
const HAFTA_MAX = 100;
/** Bo'limlar bullet shkalasi — 85..100% (canvas). */
const BOLIM_MIN = 85;
const BOLIM_BANDLARI = [90, MAQSAD];
/** Scatter — «past davomat» chegarasi, foiz. */
const XAVF_FOIZI = 90;
/** Kelish gistogrammasidagi belgi — `ARRIVAL_BUCKETS` yorlig'i. */
const KELISH_BELGISI = "09:00";

/** Maqsaddan past, lekin xavf chegarasidan yuqori — och ko'k (canvas). */
const ORTA_RANG = SEQUENTIAL_BLUE[2] ?? "var(--chart-neytral)";

const BOSH_USLUB = { color: "var(--chart-axis)" } as const;

/** Trend Y shkalasi — ma'lumotdan, 5 ga yumaloqlab; kamida 85..100. */
function trendShkalasi(trend: ReportAnalytics["trend"]): { yMin: number; ticks: number[] } {
	const qiymatlar = trend.flatMap((t) =>
		[t.rate, t.bankRate].filter((v): v is number => v !== null)
	);
	const eng = qiymatlar.length > 0 ? Math.min(...qiymatlar) : 100;
	const yMin = Math.max(0, Math.min(85, Math.floor(eng / 5) * 5));
	const qadam = 100 - yMin <= 20 ? 5 : 10;
	const ticks: number[] = [];
	for (let v = 100; v >= yMin; v -= qadam) {
		ticks.push(v);
	}
	return { yMin, ticks: ticks.reverse() };
}

/** Scatter X chegarasi — 50 ga yuqoriga yumaloqlangan (kamida 50). */
function yuqoriChegara(qiymat: number): number {
	return Math.max(50, Math.ceil(qiymat / 50) * 50);
}

/**
 * `attendance_monthly` analitik ko'rinishi (reja F2, canvas «3 — Oylik
 * davomat»):
 * KPI (5) → [trend | holatlar donuti] → holat matritsasi →
 * [kelish vaqti | hafta kunlari | kechikkanlar kelish vaqti] →
 * [scatter | bo'limlar + eng ko'p kechikkanlar].
 *
 * ⚠️⚠️ **Konvert canvasdagi hamma narsani bermaydi** — o'rnini bosuvchi
 * ko'rinishlar (kodga ergashildi):
 * - «Kechikish qanchaga» (daqiqa bucketlari) oylik konvertda YO'Q:
 *   `histogram` — kelish vaqti, `flagged` — o'sha bucketdagi kechikkan
 *   kunlar. Uchinchi karta shu `flagged` ni chizadi.
 * - Scatter uchun bitta massivda ham kechikish daqiqasi, ham davomat
 *   foizi yo'q: `topEmployees.total` (daqiqa, top-10) va `matrix.rate`
 *   `employeeId` bo'yicha KESISHTIRILADI. Foiz `matrix.days` dan qayta
 *   HISOBLANMAYDI.
 * - `byDepartment.flagged` — KELMAGAN kunlar (kechikkan xodimlar ulushi
 *   emas), ustun shunday nomlangan.
 */
export function AttendanceMonthlyAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bolimNomi = useBolimNomi();
	const k = (kalit: string, opts?: Record<string, unknown>) =>
		t(`analytics.attendance.monthly.${kalit}`, opts);
	const bosh = t("analytics.attendance.empty");
	const holatNomi = (status: string) => t(`analytics.breakdown.${status}`);
	const holatRangi = (status: string) => holatVar(ATTENDANCE_STATUS_RANG[status] ?? "neytral");

	const jamiRate = data.totals.find((x) => x.key === "rate")?.value ?? 0;
	const kechikishSoni = data.weekday.reduce((a, h) => a + h.count, 0);

	const uzrSabablari = data.breakdown.map((b, i) => ({
		key: b.key,
		value: b.count,
		// ⚠️ Birinchi (eng katta) sabab — `uzrli` rangining o'zi, qolganlari
		// neytral ramp; har sabab legendada QIYMAT bilan (relief qoidasi).
		color:
			i === 0
				? holatVar("uzrli")
				: sequentialStep(data.breakdown.length - i, data.breakdown.length + 1),
		label: t(`analytics.breakdown.${b.key}`),
	}));

	// --- Trend ---
	const shkala = trendShkalasi(data.trend);
	const bankBor = data.trend.some((x) => x.bankRate !== null);
	const trendSeriyalari = [
		{
			key: "rate",
			label: k("trendScope"),
			color: holatVar("kelgan"),
			values: data.trend.map((x) => x.rate),
			area: true,
		},
		...(bankBor
			? [
					{
						key: "bankRate",
						label: k("trendBank"),
						color: "var(--chart-axis)",
						values: data.trend.map((x) => x.bankRate),
						dashed: true,
					},
				]
			: []),
	];

	// --- Holatlar ---
	const holatlar = data.statusMix.map((h) => ({
		key: h.status,
		value: h.days,
		color: holatRangi(h.status),
		label: holatNomi(h.status),
	}));
	const jamiKun = data.statusMix.reduce((a, h) => a + h.days, 0);

	// --- Matritsa ---
	const sanalar = matritsaSanalari(data.matrix);
	const matritsaQatorlari: StatusMatrixRow[] = data.matrix.map((m) => ({
		key: m.employeeId,
		label: m.employeeName,
		cells: m.days.map((kun) => ({
			column: kun.date,
			color: holatRangi(kun.status),
			tooltip: `${m.employeeName} · ${sanaQisqa(kun.date)} · ${holatNomi(kun.status)}`,
		})),
		trailing: <Minibar rate={m.rate} />,
	}));

	// --- Gistogrammalar ---
	const kelishBinlari = data.histogram.map((b) => ({ label: b.bucket, count: b.count }));
	const kechikkanBinlari = data.histogram.map((b) => ({ label: b.bucket, count: b.flagged }));
	const belgiIndeksi = kelishBinlari.findIndex((b) => b.label === KELISH_BELGISI);
	const belgi = belgiIndeksi >= 0 ? { index: belgiIndeksi, label: k("arrivalMarker") } : undefined;

	// --- Hafta kunlari ---
	const haftaQatorlari = tartiblanganHaftaKunlari(data.weekday).map((h) => ({
		key: String(h.weekday),
		label: t(`analytics.weekday.${h.weekday}`),
		value: h.rate === null ? 0 : Math.min(HAFTA_MAX, Math.max(HAFTA_MIN, h.rate)) - HAFTA_MIN,
		valueText: k("weekdayValue", { rate: foizMatni100(h.rate), count: sonMatni(h.count) }),
		color: h.rate !== null && h.rate < MAQSAD - 3 ? holatVar("kelmagan") : holatVar("kelgan"),
	}));

	// --- Scatter ---
	const foizlar = new Map(data.matrix.map((m) => [m.employeeId, m.rate]));
	const kesishma = data.topEmployees.flatMap((e) => {
		const r = foizlar.get(e.employeeId);
		return r === undefined || r === null ? [] : [{ ...e, rate: r }];
	});
	const xMax = yuqoriChegara(Math.max(0, ...kesishma.map((e) => e.total)));
	const xChegara = xMax / 2;
	const yMin = Math.min(80, Math.floor(Math.min(100, ...kesishma.map((e) => e.rate)) / 10) * 10);
	const nuqtalar: ScatterPoint[] = kesishma.map((e) => {
		const xavf = e.rate < XAVF_FOIZI && e.total >= xChegara;
		return {
			key: e.employeeId,
			x: e.total,
			y: e.rate,
			color: xavf ? holatVar("kelmagan") : holatVar("kelgan"),
			tooltip: k("scatterPoint", {
				name: e.employeeName,
				minutes: sonMatni(e.total),
				rate: foizMatni100(e.rate),
			}),
			label: xavf ? e.employeeName : undefined,
		};
	});
	const yBelgilari = [...new Set([yMin, XAVF_FOIZI, 100])]
		.sort((a, b) => a - b)
		.map((v) => ({ value: v, label: `${v}%` }));

	// --- Eng ko'p kechikkanlar ---
	const engKop = Math.max(0, ...data.topEmployees.map((e) => e.total));

	return (
		<div data-analytics-layout="attendance_monthly" className="flex flex-col gap-4">
			<KpiRow
				kind={kind}
				totals={data.totals}
				spark={data.spark}
				items={[
					{
						key: "rate",
						format: foizMatni100,
						noSpark: true,
						hint: (
							<div className="mt-2 flex flex-col gap-1">
								<BulletBar
									value={jamiRate}
									min={KPI_MIN}
									max={100}
									target={MAQSAD}
									bands={[90]}
									color={holatVar("kelgan")}
									ariaLabel={k("rateAria", { rate: foizMatni100(jamiRate), target: MAQSAD })}
								/>
								<span className="text-xs" style={BOSH_USLUB}>
									{k("rateHint", { target: MAQSAD })}
								</span>
							</div>
						),
					},
					{
						key: "lateMin",
						// ⚠️ Qiymat soat:daqiqa — umumiy «(daq)» yorlig'i uni daqiqa deb o'qitardi.
						label: k("lateLabel"),
						format: soatDaqiqa,
						hint: (
							<span className="text-xs" style={BOSH_USLUB}>
								{k("lateHint", { count: sonMatni(kechikishSoni) })}
							</span>
						),
					},
					{ key: "absent", danger: true },
					{
						key: "excused",
						noSpark: true,
						hint: (
							<div className="mt-2">
								{uzrSabablari.length > 0 ? (
									<SegmentBar segments={uzrSabablari} height={8} />
								) : (
									<span className="text-xs" style={BOSH_USLUB}>
										{bosh}
									</span>
								)}
							</div>
						),
					},
					{ key: "earlyLeave" },
				]}
			/>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				<ChartCard
					title={k("trendTitle")}
					subtitle={k("trendSubtitle", { count: data.trend.length })}
				>
					<div data-monthly-trend>
						<LineChart
							xLabels={data.trend.map((x) => k(`monthShort.${Number(x.month.slice(5, 7))}`))}
							series={trendSeriyalari}
							yMin={shkala.yMin}
							yMax={100}
							yTicks={shkala.ticks}
							formatY={(v) => `${String(v).replace(".", ",")}%`}
							target={{ value: MAQSAD, label: k("target", { value: MAQSAD }) }}
							empty={bosh}
						/>
					</div>
				</ChartCard>

				<ChartCard title={k("statusTitle")} subtitle={k("statusSubtitle")}>
					<Donut
						segments={holatlar}
						centerValue={sonMatni(jamiKun)}
						centerLabel={t("analytics.attendance.daysUnit")}
						formatValue={sonMatni}
						empty={bosh}
					/>
				</ChartCard>
			</div>

			<ChartCard
				title={k("matrixTitle")}
				subtitle={k("matrixSubtitle", { count: data.matrix.length })}
				extra={<Legend items={holatlar.map((h) => ({ label: h.label, color: h.color }))} />}
			>
				<div data-monthly-matrix>
					<StatusMatrix
						columns={sanalar.map((sana) => ({
							key: sana,
							label: kunYorlig(sana),
							muted: damKunimi(sana),
						}))}
						rows={matritsaQatorlari}
						empty={bosh}
						ariaLabel={k("matrixAria")}
					/>
				</div>
			</ChartCard>

			<AnalyticsGrid cols={3}>
				<ChartCard title={k("arrivalTitle")} subtitle={k("arrivalSubtitle")}>
					<div data-monthly-arrival>
						<Histogram bins={kelishBinlari} marker={belgi} empty={bosh} />
					</div>
				</ChartCard>

				<ChartCard title={k("weekdayTitle")} subtitle={k("weekdaySubtitle")}>
					<div data-weekday-list>
						<HBarList
							rows={haftaQatorlari}
							max={HAFTA_MAX - HAFTA_MIN}
							target={MAQSAD - HAFTA_MIN}
							empty={bosh}
						/>
					</div>
				</ChartCard>

				<ChartCard title={k("lateArrivalTitle")} subtitle={k("lateArrivalSubtitle")}>
					<div data-monthly-late-arrival>
						<Histogram
							bins={kechikkanBinlari}
							color={holatVar("kechikkan")}
							marker={belgi}
							empty={bosh}
						/>
					</div>
				</ChartCard>
			</AnalyticsGrid>

			<AnalyticsGrid cols={2}>
				<ChartCard
					title={k("scatterTitle")}
					subtitle={k("scatterSubtitle")}
					extra={
						<Legend
							items={[
								{ label: k("scatterNormal"), color: holatVar("kelgan"), shape: "dot" },
								{ label: k("scatterRisk"), color: holatVar("kelmagan"), shape: "dot" },
							]}
						/>
					}
				>
					<div data-monthly-scatter>
						<ScatterPlot
							points={nuqtalar}
							xMin={0}
							xMax={xMax}
							yMin={yMin}
							yMax={100}
							xLabel={k("scatterX")}
							yTicks={yBelgilari}
							quadrant={{
								x: xChegara,
								y: XAVF_FOIZI,
								highlight: "bottom-right",
								label: k("scatterQuadrant"),
							}}
							empty={bosh}
						/>
					</div>
				</ChartCard>

				<ChartCard title={k("departmentsTitle")} subtitle={k("departmentsSubtitle")}>
					<div data-monthly-departments className="flex flex-col gap-2">
						{data.byDepartment.length === 0 ? (
							<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
								{bosh}
							</div>
						) : (
							<>
								<div
									className="grid grid-cols-[minmax(0,8rem)_1fr_3.5rem_3.5rem_4.5rem] gap-3 text-[10px]"
									style={BOSH_USLUB}
								>
									<span>{k("colDepartment")}</span>
									<span>{`${BOLIM_MIN}% — 100%`}</span>
									<span className="text-right">{k("colRate")}</span>
									<span className="text-right">{k("colAbsent")}</span>
									<span className="text-right">{k("colTrend")}</span>
								</div>
								{data.byDepartment.map((b) => {
									const nom = bolimNomi(b.departmentName);
									const foiz = foizMatni100(b.rate);
									const ulush = b.total > 0 ? `${Math.round((b.flagged / b.total) * 100)}%` : "—";
									return (
										<div
											key={b.departmentId ?? "none"}
											data-department-row
											className="grid grid-cols-[minmax(0,8rem)_1fr_3.5rem_3.5rem_4.5rem] items-center gap-3 text-xs"
										>
											<div className="truncate" title={nom}>
												{nom}
											</div>
											<BulletBar
												value={b.rate ?? BOLIM_MIN}
												min={BOLIM_MIN}
												max={100}
												target={MAQSAD}
												bands={BOLIM_BANDLARI}
												color={
													b.rate !== null && b.rate < XAVF_FOIZI
														? holatVar("kelmagan")
														: holatVar("kelgan")
												}
												ariaLabel={k("departmentAria", { name: nom, rate: foiz })}
											/>
											<span className="text-right tabular-nums">{foiz}</span>
											<span className="text-right tabular-nums">{ulush}</span>
											<div className="flex justify-end">
												<Sparkline
													points={b.trend}
													color={holatVar("kelgan")}
													width={72}
													height={22}
													ariaLabel={k("trendAria", { name: nom })}
												/>
											</div>
										</div>
									);
								})}
							</>
						)}
					</div>

					<div data-monthly-top className="mt-4 flex flex-col gap-2">
						<div className="font-medium text-xs">{k("topTitle")}</div>
						<div className="text-[10px]" style={BOSH_USLUB}>
							{k("topSubtitle")}
						</div>
						{data.topEmployees.length === 0 ? (
							<div className="py-2 text-center text-xs" style={BOSH_USLUB}>
								{bosh}
							</div>
						) : (
							data.topEmployees.map((e) => (
								<div
									key={e.employeeId}
									data-top-row
									className="grid grid-cols-[minmax(0,8rem)_1fr_3.5rem] items-center gap-3 text-xs"
								>
									<div className="truncate" title={e.employeeName}>
										{e.employeeName}
									</div>
									<div style={{ width: `${engKop > 0 ? (e.total / engKop) * 100 : 0}%` }}>
										<SegmentBar
											height={8}
											showLegend={false}
											segments={e.weekly.map((v, i) => ({
												key: `w${i}`,
												value: v,
												color: sequentialStep(i + 2, e.weekly.length + 1),
												label: k("weekLabel", { n: i + 1 }),
											}))}
										/>
									</div>
									<span className="text-right tabular-nums">{soatDaqiqa(e.total)}</span>
								</div>
							))
						)}
					</div>
				</ChartCard>
			</AnalyticsGrid>
		</div>
	);
}

/** Matritsa qatori oxiridagi davomat — minibar + foiz matni. */
/**
 * Shanba/yakshanba — matritsa ustuni xira (canvas). ⚠️ `getUTCDay()`:
 * `"YYYY-MM-DD"` UTC yarim tun sifatida tahlil qilinadi, lokal
 * `getDay()` G'arb zonalarida kunni bittaga siljitardi.
 */
function damKunimi(sana: string): boolean {
	const kun = new Date(`${sana}T00:00:00Z`).getUTCDay();
	return kun === 0 || kun === 6;
}

function Minibar({ rate }: { rate: number | null }) {
	const rang =
		rate === null
			? "var(--chart-grid)"
			: rate >= MAQSAD
				? holatVar("kelgan")
				: rate >= XAVF_FOIZI
					? ORTA_RANG
					: holatVar("kelmagan");

	return (
		<span className="inline-flex items-center gap-1.5">
			<span
				aria-hidden="true"
				className="block h-1.5 w-10 overflow-hidden rounded-full"
				style={{ backgroundColor: "var(--chart-grid)" }}
			>
				<span className="block h-full" style={{ width: `${rate ?? 0}%`, backgroundColor: rang }} />
			</span>
			{foizMatni100(rate)}
		</span>
	);
}
